<template>
  <div>
    <eshops-usage-chart />

    <transition mode="out-in">
      <div v-if="loading"><loading-spinner /></div>
      <div v-else class="overflow-auto">

        <h3>Onboarded eShops: {{ totalItems }}</h3>

        <paginator-bar :page="page" :per-page="limit" :total-items="totalItems" @next-page="nextPage" />

        <table class="table table-striped table-condensed table-hover">
          <thead>
          <tr>
            <th class="pointer" @click="setSort('id')">eShop ID <span class="sort-caret">{{ sortCaret('id') }}</span></th>
            <th class="pointer" @click="setSort('name')">eBay User <span class="sort-caret">{{ sortCaret('name') }}</span></th>
            <th class="pointer" @click="setSort('create_date')">Connected <span class="sort-caret">{{ sortCaret('create_date') }}</span></th>
          </tr>
          </thead>
          <tbody>
            <tr v-for="eshop in eshops" :key="eshop.id" @click.stop.prevent="gotoEshop(eshop.id)" class="pointer">
              <td><strong>{{ eshop.id }}</strong></td>
              <td>{{ eshop.name }}</td>
              <td>{{ new Date(eshop.create_date).toLocaleString() }}</td>
            </tr>
          </tbody>
        </table>

        <paginator-bar :page="page" :per-page="limit" :total-items="totalItems" @next-page="nextPage" />
      </div>
    </transition>
  </div>
</template>

<script setup>
import LoadingSpinner from "@/app/components/Utils/LoadingSpinner.vue";
import PaginatorBar from "@/app/components/Utils/PaginatorBar.vue";

import { ref, onMounted, reactive, nextTick } from 'vue'
import { useStore } from 'vuex'
import { useRouter } from "vue-router";
import EshopsUsageChart from "@/admin/components/eshops/EshopsUsageChart.vue";

const router = useRouter()
const loading = ref(false)
const eshops = ref([])
const store = useStore()
const overflowIds = reactive(new Set())
const descRefs = reactive({})

const page = ref(1)
const limit = ref(30)
const totalItems = ref(0)
const sortBy = ref('create_date')
const sortOrder = ref('desc')

onMounted(() => {
  loading.value = true
  fetchEshops()
  loading.value = false
})

async function fetchEshops() {
  const response = await store.state.api.get('/admin/eshops/', {
    params: {
      page: page.value,
      limit: limit.value,
      sortBy: sortBy.value,
      sortOrder: sortOrder.value
    }
  })
  eshops.value = response.data?.eshops || []
  totalItems.value = response.data?.total || 0
  await nextTick()
  checkOverflow()
}

async function nextPage(dir) {
  page.value += dir
  await fetchEshops()
}

function setSort(column) {
  if (sortBy.value === column) {
    sortOrder.value = sortOrder.value === 'asc' ? 'desc' : 'asc'
  } else {
    sortBy.value = column
    sortOrder.value = 'asc'
  }
  page.value = 1
  fetchEshops()
}

function sortCaret(column) {
  if (sortBy.value !== column) return ''
  return sortOrder.value === 'asc' ? '▲' : '▼'
}

function gotoEshop(eshop_id) {
  router.push({name: 'AdminEshopDetails', params: {eshopId: eshop_id}})
}

function checkOverflow() {
  for (const eshop of eshops.value) {
    const el = descRefs[eshop.id]
    if (el && el.scrollHeight > el.clientHeight) {
      overflowIds.add(eshop.id)
    }
  }
}
</script>

<style scoped>
.logo {
  width: 80px;
  height: 80px;
  border: 1px solid #abc;
}

.description-cell {
  max-width: 280px;
}

.description-wrapper.truncatable {
  cursor: pointer;
  user-select: none;
}

.description-content {
  overflow: hidden;
  max-height: 4.2em; /* ~3 lines */
  line-height: 1.4em;
  transition: max-height 0.25s ease;
}

.description-wrapper.expanded .description-content {
  max-height: 600px;
}

.description-toggle {
  display: flex;
  align-items: center;
  gap: 3px;
  margin-top: 2px;
  color: #6c757d;
  font-size: 0.75em;
}

.caret {
  font-size: 0.7em;
}

.sort-caret {
  font-size: 0.65em;
}
</style>
